import {
  convertDateToBrazilFormat,
  getReadableDuration,
  PersonNameTableCell,
} from '@repo/ui';
import {
  TCE_LISTING_TYPE,
  TceListingType,
} from 'domain/value-objects/tce-listing-type';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useMemo } from 'react';
import { Badge } from 'reactstrap';

import { TceSignatureStatusBadge } from '../TceSignatureStatusBadge';

export const useTceManagementColumns = (listingType?: TceListingType) => {
  const { getLegends } = useLegends();

  const shouldShowSignatureStatus =
    listingType === TCE_LISTING_TYPE.ACTIVE ||
    listingType === TCE_LISTING_TYPE.EXPIRED;

  const shouldShowInternshipInfo =
    listingType !== TCE_LISTING_TYPE.STUDENTS_WITHOUT_TCE;

  return useMemo(
    () => [
      {
        id: 'studentName',
        name: 'Estudante',
        selector: (row: {
          student?: { name: string; cpf: string; photo?: string };
        }) => row.student?.name || '-',
        cell: (row: {
          student?: { name: string; cpf: string; photo?: string };
        }) => {
          if (!row.student) return '-';
          return (
            <PersonNameTableCell
              imgSrc={row.student.photo}
              name={row.student.name}
              cpf={row.student.cpf}
            />
          );
        },
        sortable: true,
        wrap: true,
        width: shouldShowInternshipInfo ? '250px' : '25%',
      },
      {
        id: 'studentRa',
        name: 'RA',
        selector: (row: { student?: { ra: string } }) => row.student?.ra || '-',
        width: shouldShowInternshipInfo ? '90px' : '25%',
        center: true,
      },
      {
        id: 'groupName',
        name: getLegends('GRUPO'),
        selector: (row: { group?: { name: string; hasTceModel?: boolean } }) =>
          row.group?.name || '-',
        cell: (row: { group?: { name: string; hasTceModel?: boolean } }) => {
          if (!row.group) return '-';
          const showTceBadge = row.group.hasTceModel === false;

          return (
            <div className="d-flex flex-column gap-1">
              <span>{row.group.name}</span>
              {showTceBadge && (
                <Badge
                  color="warning"
                  className="d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '0.7rem', width: 'fit-content' }}
                >
                  <i className="ri-alert-line"></i>
                  Falta Modelo de TCE
                </Badge>
              )}
            </div>
          );
        },
        sortable: true,
        wrap: true,
        width: shouldShowInternshipInfo ? undefined : '45%',
      },
      ...(shouldShowInternshipInfo
        ? [
            {
              id: 'internshipLocation',
              name: 'Local de Estágio',
              selector: (row: { internshipLocation?: { name: string } }) =>
                row.internshipLocation?.name || '-',
              wrap: true,
            },
            {
              id: 'period',
              name: 'Período',
              cell: (row: {
                internshipPeriod?: { startDate: string; endDate: string };
              }) => {
                if (
                  !row.internshipPeriod?.startDate ||
                  !row.internshipPeriod?.endDate
                ) {
                  return '-';
                }

                const startDate = convertDateToBrazilFormat(
                  row.internshipPeriod.startDate,
                );
                const endDate = convertDateToBrazilFormat(
                  row.internshipPeriod.endDate,
                );
                const duration = getReadableDuration(
                  row.internshipPeriod.startDate,
                  row.internshipPeriod.endDate,
                );

                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <div>{`${startDate} - ${endDate}`}</div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                      {duration}
                    </div>
                  </div>
                );
              },
              sortable: true,
              width: '220px',
            },
          ]
        : []),
      ...(shouldShowSignatureStatus
        ? [
            {
              id: 'statusSignature',
              name: 'Status de Assinatura',
              cell: (row: {
                statusSignature?: {
                  studentSigned: boolean;
                  institutionSigned: boolean;
                  grantorSigned: boolean;
                };
              }) => {
                if (!row.statusSignature) return '-';
                return <TceSignatureStatusBadge status={row.statusSignature} />;
              },
              wrap: true,
              width: '200px',
            },
          ]
        : []),
    ],
    [getLegends, shouldShowSignatureStatus, shouldShowInternshipInfo],
  );
};
